import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header, Footer } from "@/components/SiteChrome";
import { useCart, cart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

export default function Cart() {
    const items = useCart();
    const { user } = useAuth();
    const [products, setProducts] = useState({});
    const [checkingOut, setCheckingOut] = useState(false);
    const [email, setEmail] = useState(user?.email || "");
    const navigate = useNavigate();

    useEffect(() => {
        setEmail(user?.email || "");
    }, [user]);

    useEffect(() => {
        if (items.length === 0) return;
        api.get("/products", { params: { limit: 300 } }).then(({ data }) => {
            const map = {};
            data.items.forEach((p) => { map[p.slug] = p; });
            setProducts(map);
        });
    }, [items.length]);

    const rows = useMemo(() => items.map((i) => ({ ...i, p: products[i.slug] })).filter((r) => r.p), [items, products]);
    const subtotal = rows.reduce((s, r) => s + r.p.price * r.qty, 0);
    const shipping = subtotal > 0 ? (subtotal > 250 ? 0 : 24) : 0;

    // Persist a correlation_id across checkout attempts to make server-side idempotent
    const [correlationId] = useState(() => {
        const existing = sessionStorage.getItem("uor_checkout_correlation");
        if (existing) return existing;
        const id = (crypto?.randomUUID?.() || `uor-${Date.now()}-${Math.random().toString(36).slice(2)}`);
        sessionStorage.setItem("uor_checkout_correlation", id);
        return id;
    });

    const checkout = async () => {
        if (rows.length === 0 || checkingOut) return;  // idempotency guard
        setCheckingOut(true);
        try {
            const { data } = await api.post("/cart/shopify-checkout", {
                items: rows.map((r) => ({ slug: r.slug, qty: r.qty })),
                origin_url: window.location.origin,
                email: email || null,
                correlation_id: correlationId,
            });
            const warn = data.warnings || {};
            const skipped = [...(warn.missing_variant || []), ...(warn.out_of_stock || [])];
            if (skipped.length > 0) {
                const names = skipped.map((s) => s.name || s.slug).join(", ");
                if (!window.confirm(`Some items can't be transferred to Shopify and will be skipped: ${names}. Continue to Shopify with the ${data.transferable_line_count} remaining items?`)) {
                    setCheckingOut(false);
                    return;
                }
            }
            // If Shopify has server-side verified the cart, clear the local cart before redirect.
            // If unverified (legacy permalink), leave local cart intact so nothing is lost on failure —
            // the customer's local cart will be cleared when they return via /payment/success or manually.
            if (data.verified === true) {
                cart.clear();
                sessionStorage.removeItem("uor_checkout_correlation");
                toast.success("Cart transferred to Shopify — redirecting…");
            } else {
                toast.info("Redirecting to Shopify checkout…");
            }
            window.location.href = data.checkout_url;
        } catch (e) {
            const detail = e.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || "Checkout failed";
            toast.error(msg);
            setCheckingOut(false);
            // On any failure, local cart is intact — user can retry safely with same correlation_id.
        }
    };

    return (
        <div className="min-h-screen bg-[hsl(var(--dust-50))]" data-testid="cart-page">
            <Header />
            <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
                <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">Your build</div>
                <h1 className="mt-2 font-display text-5xl uppercase italic md:text-6xl text-[hsl(var(--ink))]">Cart</h1>

                <div className="mt-6 border-2 border-[hsl(var(--rust))] bg-[hsl(var(--rust))]/10 p-4" data-testid="cart-test-mode-banner">
                    <div className="flex flex-wrap items-start gap-4">
                        <div className="flex-shrink-0 border-2 border-[hsl(var(--rust))] bg-[hsl(var(--rust))] px-3 py-1 font-heading text-xs font-bold uppercase tracking-wider text-[hsl(var(--dust-50))]">
                            Shopify checkout
                        </div>
                        <div className="min-w-0 flex-1 text-sm text-[hsl(var(--ink))]">
                            <div className="font-heading text-sm uppercase tracking-wider">
                                Payments handled by Urban Off-Road on Shopify.
                            </div>
                            <div className="mt-2 font-mono-brand text-xs text-[hsl(var(--ink-soft))]">
                                Your build cart transfers to <code className="text-[hsl(var(--ink))]">urbanoff-road.com/cart</code> for secure checkout — shipping, taxes, and payment are handled by Shopify. Items without a Shopify variant will be flagged before you continue.
                            </div>
                        </div>
                    </div>
                </div>

                {rows.length === 0 ? (
                    <div className="mt-12 border-2 border-dashed border-[hsl(var(--dust-300))] p-16 text-center" data-testid="cart-empty">
                        <div className="font-display text-3xl uppercase italic text-[hsl(var(--ink))]">Nothing loaded up yet.</div>
                        <Link to="/shop" className="mt-6 inline-block btn-primary">Start exploring →</Link>
                    </div>
                ) : (
                    <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_360px]">
                        <div className="divide-y divide-[hsl(var(--dust-300))] border-y border-[hsl(var(--dust-300))]">
                            {rows.map((r) => (
                                <div key={r.slug} className="flex gap-4 py-5" data-testid={`cart-row-${r.slug}`}>
                                    <div className="h-24 w-24 flex-shrink-0 border border-[hsl(var(--ink))] bg-[hsl(var(--dust-100))]">
                                        {r.p.images?.[0] ? (
                                            <img src={r.p.images[0]} alt={r.p.name} className="h-full w-full object-contain p-1" />
                                        ) : (
                                            <div className="flex h-full items-center justify-center font-mono-brand text-[10px] text-[hsl(var(--ink-soft))]">{r.p.sku}</div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <div className="font-mono-brand text-xs uppercase text-[hsl(var(--ink-soft))]">{r.p.sku}</div>
                                        <Link to={`/shop/${r.slug}`} className="font-heading text-lg uppercase text-[hsl(var(--ink))] hover:underline">
                                            {r.p.name}
                                        </Link>
                                        <div className="mt-2 flex items-center gap-3">
                                            <div className="flex items-stretch border border-[hsl(var(--ink))]">
                                                <button onClick={() => cart.setQty(r.slug, r.qty - 1)} className="px-3" data-testid={`cart-minus-${r.slug}`}>−</button>
                                                <span className="flex w-8 items-center justify-center font-mono-brand text-sm">{r.qty}</span>
                                                <button onClick={() => cart.setQty(r.slug, r.qty + 1)} className="px-3" data-testid={`cart-plus-${r.slug}`}>+</button>
                                            </div>
                                            <button onClick={() => cart.remove(r.slug)} className="font-mono-brand text-xs uppercase text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--danger))]" data-testid={`cart-remove-${r.slug}`}>
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                    <div className="font-display text-2xl text-[hsl(var(--ink))]">${(r.p.price * r.qty).toFixed(2)}</div>
                                </div>
                            ))}
                        </div>

                        <aside className="h-fit border-2 border-[hsl(var(--ink))] bg-[hsl(var(--dust-300))] p-6 text-[hsl(var(--dust-50))]">
                            <div className="font-heading text-sm uppercase tracking-wider">Order summary</div>
                            <dl className="mt-4 space-y-2 text-sm">
                                <div className="flex justify-between"><dt>Subtotal</dt><dd className="font-mono-brand" data-testid="cart-subtotal">${subtotal.toFixed(2)}</dd></div>
                                <div className="flex justify-between"><dt>Shipping</dt><dd className="font-mono-brand">{shipping === 0 ? "Free" : `$${shipping}`}</dd></div>
                                <div className="mt-3 flex justify-between border-t border-[hsl(var(--dust-50))]/30 pt-3 font-display text-2xl">
                                    <dt>Total</dt><dd>${(subtotal + shipping).toFixed(2)}</dd>
                                </div>
                            </dl>

                            <label className="mt-6 block font-mono-brand text-[10px] uppercase tracking-wider text-[hsl(var(--dust-100))]">Email for receipt</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@rig.com"
                                className="mt-1 w-full border border-[hsl(var(--ink))] bg-[hsl(var(--dust-50))] px-3 py-2 font-mono-brand text-sm text-[hsl(var(--ink))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                data-testid="cart-email"
                            />

                            <button
                                onClick={checkout}
                                disabled={checkingOut}
                                className="mt-6 w-full border-2 border-[hsl(var(--ink))] bg-[hsl(var(--primary))] py-4 font-heading text-sm uppercase tracking-wider text-[hsl(var(--ink))] hover:bg-[hsl(var(--primary-hover))] disabled:opacity-60"
                                data-testid="cart-checkout"
                            >
                                {checkingOut ? "Sending you to Shopify…" : "Continue to Shopify checkout →"}
                            </button>
                            <p className="mt-3 text-center font-mono-brand text-[10px] uppercase tracking-wider text-[hsl(var(--dust-100))]">
                                Secure checkout · Shopify · urbanoff-road.com
                            </p>
                        </aside>
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
}
